/**
 * The one place this app talks to the API.
 *
 * `features/*\/api.ts` are the only callers; routes and components never
 * reach for `fetch` themselves. That is what keeps the refresh handling,
 * the problem envelope and the idempotency rules in one auditable place
 * instead of thirty.
 */

import { ApiError, problemFrom } from "@/lib/api/problem";

/** Injected so tests pass a plain stub. MSW buys nothing here and its
 *  interception under Bun is version-sensitive. */
export type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

export interface ClientOptions {
  fetch?: Fetcher;
  /**
   * Called when the session is gone for good — refresh was tried and
   * refused. The shell redirects to `/login?redirect=<here>`; tests assert
   * it was called rather than navigating.
   */
  onSessionExpired?: () => void;
  /** Overridable so a test can assert the key is stable across a retry. */
  newIdempotencyKey?: () => string;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Sent as `If-Match`. Absent on a write the server requires it for is a
   * 428, which is the server telling the client it forgot — not something
   * to paper over here.
   */
  ifMatch?: string;
  query?: Record<string, string | number | boolean | undefined>;
}

/** A response body plus the ETag it arrived with, for the write that
 *  follows the read. */
export interface Envelope<T> {
  data: T;
  etag: string | undefined;
}

const SAFE_METHODS = new Set(["GET", "HEAD"]);

export class ApiClient {
  private readonly fetcher: Fetcher;
  private readonly onSessionExpired: (() => void) | undefined;
  private readonly newIdempotencyKey: () => string;

  /**
   * Per-instance rather than module-level so tests are isolated from each
   * other. Production has one client, so the behaviour is identical.
   */
  private refreshInFlight: Promise<boolean> | null = null;

  constructor(options: ClientOptions = {}) {
    this.fetcher = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.onSessionExpired = options.onSessionExpired;
    this.newIdempotencyKey = options.newIdempotencyKey ?? (() => crypto.randomUUID());
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return (await this.send<T>(path, options)).data;
  }

  /**
   * The same request, with the ETag the response carried.
   *
   * Editing needs it: `PATCH` requires `If-Match`, and answers 428 when
   * it is absent rather than guessing. Exposed as its own method rather
   * than by handing callers the `Response` — the header is the only part
   * of it anything outside this file has ever needed, and returning the
   * whole thing invites a `features/` module to start reading status
   * codes and re-deriving the error handling that lives here.
   */
  async requestWithEtag<T>(path: string, options: RequestOptions = {}): Promise<Envelope<T>> {
    return this.send<T>(path, options);
  }

  /**
   * The raw `Response`, session refresh and error handling still applied,
   * but with no JSON decode.
   *
   * The one caller today is audit export: the same route answers either a
   * streamed CSV body or a JSON job envelope depending on row count and
   * format, and `request`'s `response.json()` can't speak for a body that
   * might not be JSON at all. Kept narrow rather than becoming the default
   * — `request`/`requestWithEtag` cover everything that doesn't need to
   * branch on content type.
   */
  async requestRaw(path: string, options: RequestOptions = {}): Promise<Response> {
    return this.sendRaw(path, options);
  }

  private async send<T>(path: string, options: RequestOptions = {}): Promise<Envelope<T>> {
    const response = await this.sendRaw(path, options);
    const etag = response.headers.get("ETag") ?? undefined;
    if (response.status === 204 || response.headers.get("Content-Length") === "0") {
      return { data: undefined as T, etag };
    }
    return { data: (await response.json()) as T, etag };
  }

  private async sendRaw(path: string, options: RequestOptions = {}): Promise<Response> {
    const method = options.method ?? "GET";
    const url = withQuery(path, options.query);

    // Generated ONCE, out here, and reused by the retry below. Minting it
    // inside the send would make the second attempt a different request as
    // far as the server is concerned — creating the duplicate the header
    // exists to prevent.
    const idempotencyKey = SAFE_METHODS.has(method) ? undefined : this.newIdempotencyKey();

    // A file upload is multipart, and multipart is the one body this
    // client must not touch: `JSON.stringify(formData)` yields "{}", and
    // setting `Content-Type` by hand omits the boundary the browser
    // generates, so the server sees a malformed part. Left to `fetch`,
    // both are correct.
    const isMultipart = typeof FormData !== "undefined" && options.body instanceof FormData;

    const send = (): Promise<Response> =>
      this.fetcher(url, {
        method,
        // The session is two HttpOnly cookies. Without this they are not
        // attached and every request is anonymous.
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...(options.body === undefined || isMultipart
            ? {}
            : { "Content-Type": "application/json" }),
          ...(idempotencyKey === undefined ? {} : { "Idempotency-Key": idempotencyKey }),
          ...(options.ifMatch === undefined ? {} : { "If-Match": options.ifMatch }),
          // Last, so a caller can override the generated idempotency key
          // with one of its own. An upload needs that: the key here is
          // minted per *call*, so a double-clicked Upload button would
          // send two keys and create two imports.
          ...options.headers,
        },
        ...(options.body === undefined
          ? {}
          : { body: isMultipart ? (options.body as FormData) : JSON.stringify(options.body) }),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      });

    let response = await send();

    if (response.status === 401 && !isAuthPath(path)) {
      // Retrying a POST is provably safe here: `current_identity` is a
      // FastAPI dependency, so a 401 is raised before any handler runs and
      // nothing happened server-side. The idempotency key above covers the
      // case where that ever stops being true.
      const refreshed = await this.refresh();
      if (refreshed) {
        response = await send();
      }
      if (!refreshed || response.status === 401) {
        this.onSessionExpired?.();
        throw new ApiError(await problemFrom(response));
      }
    }

    if (!response.ok) {
      // Note what is NOT here: a 503 retry. Redis being unreachable makes
      // authentication fail closed on purpose, and retrying hammers a
      // system already in an incident. The user gets an error with a
      // Retry button and decides for themselves.
      throw new ApiError(await problemFrom(response));
    }

    return response;
  }

  /**
   * One refresh, however many requests hit a 401 at once.
   *
   * `??=` reads and assigns synchronously — before any `await` — so N
   * parallel 401s all see the same promise and only the first one POSTs.
   * Clearing it in `finally` (after settling, not before) is what lets the
   * *next* expiry try again rather than reusing a resolved answer forever.
   */
  private refresh(): Promise<boolean> {
    this.refreshInFlight ??= this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<boolean> {
    try {
      const response = await this.fetcher("/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      return response.ok;
    } catch {
      // The network is down, not the session. Treated as "could not
      // refresh" so the caller surfaces an error rather than hanging.
      return false;
    }
  }
}

/** `/auth/refresh` returning 401 must not trigger another refresh. */
function isAuthPath(path: string): boolean {
  return path.startsWith("/auth/refresh") || path.startsWith("/auth/login");
}

export function withQuery(
  path: string,
  query: Record<string, string | number | boolean | undefined> | undefined,
): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised === "" ? path : `${path}?${serialised}`;
}

/**
 * The app's client. Redirects on a dead session, which is the one thing a
 * test never wants, so tests construct their own `ApiClient` with a stub.
 */
export const client = new ApiClient({
  onSessionExpired: () => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    window.location.assign(`/login?redirect=${encodeURIComponent(returnTo)}`);
  },
});
